/**
 * El alta por invitación (20260925188000): con qué plan nace el negocio de quien fue invitado, y a
 * dónde aterriza.
 *
 * ⭐ LA INVITACIÓN ES LA AUTORIZACIÓN. Smarty invita un correo con un plan (y, para el courier, un
 *    papel); `create-store` la consume. La pantalla sólo la LEE para hacer las preguntas que
 *    corresponden —el WhatsApp de un socio, no el rubro de una tienda— y para saber a dónde llevarlo.
 *    El plan no viaja en la URL: la base le muestra a cada persona sólo la invitación de su correo.
 */
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/config/routes.config';
import { errorDeEdgeFunction } from '@/lib/errorDeEdgeFunction';

export interface MiInvitacion {
  plan: 'drop' | 'basic' | 'almacen';
  papel: 'courier' | null;
}

export async function miInvitacionDeNegocio(): Promise<MiInvitacion | null> {
  const { data, error } = await supabase
    .from('invitaciones_de_negocio')
    .select('plan, papel')
    .is('usada_en', null)
    .gt('vence_en', new Date().toISOString())
    .maybeSingle();
  if (error) return null;
  return (data as MiInvitacion | null) ?? null;
}

/**
 * Dónde crea su negocio quien tiene una invitación pendiente. El revendedor, con `?plan=drop`: no se le
 * pregunta el rubro y se le pide su WhatsApp, como en `/registro?plan=drop`.
 */
export function dondeSeCreaElNegocio(invitacion: MiInvitacion): string {
  return invitacion.plan === 'drop' ? '/stores/new?plan=drop' : '/stores/new';
}

/**
 * Si la cuenta de quien está entrando ya tiene contraseña (20261028031500). La base responde sí o no, sólo de
 * la propia cuenta.
 *
 * 🚨 POR QUÉ SE PREGUNTA. El enlace de la invitación abre la sesión SIN contraseña. El 27-oct dos drops
 *    salieron de `/registro`, el panel los llevó a crear su tienda y al volver no tenían con qué entrar.
 *
 * `null` si no se pudo saber: no se frena a nadie por una consulta que falló.
 */
export async function miCuentaTieneClave(): Promise<boolean | null> {
  const { data, error } = await supabase.rpc('fn_mi_cuenta_tiene_clave');
  if (error || typeof data !== 'boolean') return null;
  return data;
}

/**
 * Dónde pone su contraseña el invitado que todavía no la tiene: el paso de la clave de `/registro`, en modo
 * invitación. Al guardarla, `/registro` lo lleva a crear su negocio (con `?plan=drop` si es revendedor).
 */
export function dondeSePoneLaClave(invitacion: MiInvitacion): string {
  return invitacion.plan === 'drop' ? '/registro?invitacion=1&plan=drop' : '/registro?invitacion=1';
}

/**
 * El primer lugar del negocio recién nacido: el revendedor, el catálogo; el courier, su perfil (tarifa,
 * zonas y motorizados, que es lo que le falta para que le asignen pedidos); el proveedor o almacén, Mi
 * almacén; una tienda, el inicio con la bienvenida.
 */
export function destinoDelAlta(invitacion: MiInvitacion | null, esRevendedor: boolean): string {
  if (esRevendedor || invitacion?.plan === 'drop') return ROUTES.DROPSHIPPING_SMARTYDROP;
  if (invitacion?.papel === 'courier') return ROUTES.DROPSHIPPING_SMARTYDROP_COURIERS;
  if (invitacion?.plan === 'almacen') return ROUTES.DROPSHIPPING_SMARTYDROP_ALMACEN;
  return '/?welcome=1';
}

/** Lo que responde `abrir-invitacion`: un enlace para entrar ya, o a dónde ir y por qué. */
export type AperturaDeInvitacion =
  | { enlace: string }
  | { destino: string; motivo: string }
  | { vencida: true }
  | { error: string };

/**
 * El botón «Aceptar invitación» del correo lleva a `/invitacion/<id>`, y esto pide el enlace de entrada
 * en ese momento: el de Supabase vence a los 10 minutos, la invitación a los 30 días (30-sep).
 */
export async function abrirInvitacion(id: string): Promise<AperturaDeInvitacion> {
  const { data, error } = await supabase.functions.invoke<AperturaDeInvitacion>('abrir-invitacion', { body: { id } });
  if (error || !data) return { error: await errorDeEdgeFunction(error, 'No se pudo abrir la invitación.') };
  return data;
}
